import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import config from '../../../config/environment';

const navyLogo = 'https://BelvoirCastle.b-cdn.net/Logos/navy_logo_stacked.webp';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.offWhite};
  color: ${props => props.theme.colors.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
`;

const Header = styled.header`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
`;

const LogoImg = styled.img`
  height: 72px;
  width: auto;
  display: block;

  @media (max-width: 640px) {
    height: 58px;
  }
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  padding: 44px 48px 40px;
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 4px;
  box-shadow: 0 18px 48px rgba(16, 29, 64, 0.07);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.theme.colors.accent};
  }

  @media (max-width: 640px) {
    padding: 32px 22px 28px;
  }
`;

const Kicker = styled.p`
  margin: 0 0 10px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
`;

const Title = styled.h1`
  margin: 0 0 14px;
  font-size: 2.35rem;
  line-height: 1.15;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};

  @media (max-width: 640px) {
    font-size: 1.85rem;
  }
`;

const Description = styled.p`
  margin: 0 0 22px;
  font-size: 1.02rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  font-family: 'Inter', system-ui, sans-serif;
`;

const OfferRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 0 0 28px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const OfferChip = styled.div`
  padding: 14px 16px;
  background: ${props => props.theme.colors.offWhite};
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 4px;
`;

const OfferLabel = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 4px;
`;

const OfferValue = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div``;

const Label = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
  color: ${props => props.theme.colors.primary};
  font-family: 'Inter', system-ui, sans-serif;
`;

const fieldStyles = `
  width: 100%;
  padding: 13px 14px;
  border-radius: 4px;
  border: 1px solid rgba(16, 29, 64, 0.16);
  background: #fff;
  color: #101d40;
  font-size: 1rem;
  font-family: 'Inter', system-ui, sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(16, 29, 64, 0.38);
  }

  &:focus {
    outline: none;
    border-color: #9d6d49;
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.16);
  }
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Select = styled.select`
  ${fieldStyles}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23101d40' d='M1.4.6 6 5.2 10.6.6 12 2 6 8 0 2z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
`;

const Button = styled.button`
  margin-top: 6px;
  padding: 15px 18px;
  border-radius: 4px;
  border: none;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  letter-spacing: 0.04em;
  font-size: 0.95rem;
  background: ${props => props.theme.colors.primary};
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 4px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.95rem;
  line-height: 1.55;
  font-weight: 500;
  border: 1px solid ${props => (props.error ? 'rgba(176, 42, 42, 0.28)' : 'rgba(46, 125, 50, 0.28)')};
  background: ${props => (props.error ? 'rgba(176, 42, 42, 0.06)' : 'rgba(46, 125, 50, 0.06)')};
  color: ${props => (props.error ? '#8a1f1f' : '#1b5e20')};

  a {
    color: ${props => props.theme.colors.primary};
    word-break: break-all;
  }
`;

const Hint = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(16, 29, 64, 0.55);
  font-family: 'Inter', system-ui, sans-serif;
`;

const Footnote = styled.p`
  margin: 22px 0 0;
  font-size: 0.82rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.55);
  font-family: 'Inter', system-ui, sans-serif;

  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: none;
    font-weight: 600;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const schools = [
  'Keyworth Choir',
  'Burton Joyce Brass Band',
  'Harlaxton/Denton Choir',
  'Orston Primary School',
  'Carlton Male Voice Choir',
  'Harrowby Singers',
  'Melton Band',
  'Oakham School',
  'Hickling Choir',
  'Bingham Community Band',
  'Global Harmony Choir',
  'Cranmer Singers',
  'Vintage Singers',
  'Other'
];

const ChoirDiscount = () => {
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    child_name: '',
    school: '',
    other_group: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    code: null,
    bookingUrl: null,
    message: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, code: null, bookingUrl: null, message: null });

    const submissionData = {
      ...formData,
      school: formData.school === 'Other' ? formData.other_group.trim() : formData.school
    };

    if (formData.school === 'Other' && !formData.other_group.trim()) {
      setStatus({
        submitting: false,
        error: 'Please enter the name of your choir group.',
        success: false,
        code: null,
        bookingUrl: null,
        message: null
      });
      return;
    }

    try {
      const response = await fetch(`${config.apiEndpoint}/belvoir/v1/choir-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(submissionData)
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data?.message || 'Unable to generate your discount code. Please try again.');
      }

      setStatus({
        submitting: false,
        error: data.email_sent === false
          ? (data.email_error || data.message || 'Your code was created but the email could not be sent.')
          : null,
        success: true,
        code: data.code,
        bookingUrl: data.booking_url || null,
        message: data.message || 'Your discount code has been emailed to you.'
      });

      setFormData({
        parent_name: '',
        email: '',
        child_name: '',
        school: '',
        other_group: ''
      });
    } catch (error) {
      setStatus({
        submitting: false,
        error: error.message || 'Something went wrong. Please try again.',
        success: false,
        code: null,
        bookingUrl: null,
        message: null
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Choir Discount | Belvoir Castle</title>
        <meta name="description" content="Register to receive the supporter discount when attending a Christmas choir performance." />
      </Helmet>
      <PageWrapper>
        <Header>
          <LogoImg src={navyLogo} alt="Belvoir Castle" />
        </Header>
        <Card>
          <Kicker>Christmas Choir Performances</Kicker>
          <Title>Choir supporter discount</Title>
          <Description>
            Families attending a Christmas choir performance receive 20% off plus one free Adult ticket.
            Tell us who you are supporting and we will email your unique booking link and discount code.
          </Description>

          <OfferRow>
            <OfferChip>
              <OfferLabel>Ticket discount</OfferLabel>
              <OfferValue>20% off</OfferValue>
            </OfferChip>
            <OfferChip>
              <OfferLabel>Complimentary ticket</OfferLabel>
              <OfferValue>1 × Adult</OfferValue>
            </OfferChip>
          </OfferRow>

          <Form onSubmit={handleSubmit}>
            <Field>
              <Label htmlFor="parent_name">Your full name</Label>
              <Input
                id="parent_name"
                name="parent_name"
                value={formData.parent_name}
                onChange={handleChange}
                placeholder="e.g. Sarah Thompson"
                required
              />
            </Field>

            <Field>
              <Label htmlFor="email">Email address for the code</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
              <Hint>We’ll send the discount code, unique booking link and choir updates here.</Hint>
            </Field>

            <Field>
              <Label htmlFor="child_name">Performer’s full name</Label>
              <Input
                id="child_name"
                name="child_name"
                value={formData.child_name}
                onChange={handleChange}
                placeholder="Child you’re supporting"
                required
              />
            </Field>

            <Field>
              <Label htmlFor="school">School or choir group</Label>
              <Select
                id="school"
                name="school"
                value={formData.school}
                onChange={handleChange}
                required
              >
                <option value="">Select a group</option>
                {schools.map((school) => (
                  <option value={school} key={school}>{school}</option>
                ))}
              </Select>
              {formData.school === 'Other' && (
                <div style={{ marginTop: '14px' }}>
                  <Label htmlFor="other_group">Please specify your choir group</Label>
                  <Input
                    id="other_group"
                    name="other_group"
                    value={formData.other_group}
                    onChange={handleChange}
                    placeholder="Enter choir group name"
                    required={formData.school === 'Other'}
                  />
                </div>
              )}
            </Field>

            <Button type="submit" disabled={status.submitting}>
              {status.submitting ? 'Sending…' : 'Email my discount code'}
            </Button>

            {status.error && (
              <StatusMessage error>{status.error}</StatusMessage>
            )}
            {status.success && (
              <StatusMessage>
                {status.message}{' '}
                {status.code && (
                  <>
                    <br />
                    <strong>Code:</strong> {status.code}
                  </>
                )}
                {status.bookingUrl && (
                  <>
                    <br />
                    <strong>Unique booking link:</strong>{' '}
                    <a href={status.bookingUrl}>{status.bookingUrl}</a>
                  </>
                )}
              </StatusMessage>
            )}
          </Form>

          <Footnote>
            The booking link can only be completed once and should not be shared.
            Questions? Email <a href="mailto:info@belvoircastle.com">info@belvoircastle.com</a>.
          </Footnote>
        </Card>
      </PageWrapper>
    </>
  );
};

export default ChoirDiscount;
